// DashboardIntelBrief.js - Dashboard Variation B: "Intelligence Brief"
// Document-style layout with executive summary focus

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    getCommandCenterSummary,
    getRiskLevelLabel,
    formatRelativeTime,
    getSeverityColor
} from '../../services/commandCenterService';
import { SkeletonDashboard } from '../../components/common';
import './DashboardIntelBrief.css';

// Classification banner component
function ClassificationBanner({ level = 'UNCLASSIFIED' }) {
    return (
        <div className={`ib-classification ${level.toLowerCase()}`}>
            {level}
        </div>
    );
}

// Key finding bullet point
function KeyFinding({ finding, index }) {
    return (
        <div className="ib-key-finding">
            <span className="finding-bullet" style={{ color: getSeverityColor(finding.severity) }}>
                ▪
            </span>
            <div className="finding-content">
                <strong>{finding.title}</strong>
                {finding.asset && <span className="finding-asset">({finding.asset})</span>}
                {finding.description && (
                    <p className="finding-desc">{finding.description}</p>
                )}
            </div>
        </div>
    );
}

// Metric table row
function MetricRow({ label, value, change, severity }) {
    const getChangeIndicator = (change) => {
        if (!change) return null;
        const isPositive = change > 0;
        return (
            <span className={`change-indicator ${isPositive ? 'positive' : 'negative'}`}>
                {isPositive ? '↑' : '↓'} {Math.abs(change)}
            </span>
        );
    };

    return (
        <tr className="ib-metric-row">
            <td className="metric-label">{label}</td>
            <td className="metric-value" style={severity ? { color: getSeverityColor(severity) } : {}}>
                {value}
            </td>
            <td className="metric-change">{getChangeIndicator(change)}</td>
        </tr>
    );
}

// Asset status row
function AssetStatusRow({ asset, onClick }) {
    const getStatusClass = (level) => {
        if (level === 'critical') return 'status-critical';
        if (level === 'atRisk') return 'status-atrisk';
        if (level === 'caution') return 'status-caution';
        return 'status-healthy';
    };

    return (
        <tr className="ib-asset-row" onClick={onClick}>
            <td className="asset-name">{asset.value}</td>
            <td className="asset-type">{asset.assetType?.toUpperCase()}</td>
            <td className={`asset-status ${getStatusClass(asset.riskLevel)}`}>
                {getRiskLevelLabel(asset.riskLevel)}
            </td>
            <td className="asset-findings">
                <span className="finding-count critical">{asset.vulnCounts?.critical || 0}</span>
                <span className="finding-count high">{asset.vulnCounts?.high || 0}</span>
                <span className="finding-count medium">{asset.vulnCounts?.medium || 0}</span>
            </td>
            <td className="asset-last-scan">{formatRelativeTime(asset.lastScanAt)}</td>
        </tr>
    );
}

// Recommendation item
function Recommendation({ rec, index }) {
    const getPriorityLabel = (priority) => {
        if (priority === 1) return 'IMMEDIATE';
        if (priority === 2) return 'URGENT';
        if (priority === 3) return 'MODERATE';
        return 'ROUTINE';
    };

    const getPriorityClass = (priority) => {
        if (priority === 1) return 'priority-immediate';
        if (priority === 2) return 'priority-urgent';
        if (priority === 3) return 'priority-moderate';
        return 'priority-routine';
    };

    return (
        <div className="ib-recommendation">
            <div className={`rec-priority ${getPriorityClass(index + 1)}`}>
                {getPriorityLabel(index + 1)}
            </div>
            <div className="rec-content">
                <div className="rec-title">{rec.title}</div>
                <div className="rec-details">
                    {rec.asset && <span className="rec-asset">Target: {rec.asset}</span>}
                    {rec.severity && (
                        <span className="rec-severity" style={{ color: getSeverityColor(rec.severity) }}>
                            {rec.severity?.toUpperCase()}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

function DashboardIntelBrief() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        summary: {},
        assets: [],
        topRisks: [],
        trends: {}
    });

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const result = await getCommandCenterSummary();
                if (result.success) {
                    setData({
                        summary: result.summary || {},
                        assets: result.assets || [],
                        topRisks: result.topRisks || [],
                        trends: result.trends || {}
                    });
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const { summary, assets, topRisks } = data;

    // Calculate assessment
    const overallAssessment = useMemo(() => {
        const score = summary.overallRiskScore || 0;
        if (score >= 80) return { level: 'LOW RISK', class: 'low', description: 'Security posture is strong. Continue routine monitoring.' };
        if (score >= 60) return { level: 'MODERATE RISK', class: 'moderate', description: 'Some vulnerabilities require attention. Remediation recommended.' };
        if (score >= 40) return { level: 'ELEVATED RISK', class: 'elevated', description: 'Multiple high-severity issues detected. Immediate action required.' };
        return { level: 'CRITICAL RISK', class: 'critical', description: 'Critical vulnerabilities present immediate threat. Emergency response needed.' };
    }, [summary.overallRiskScore]);

    // Generate key findings
    const keyFindings = useMemo(() => {
        return topRisks.slice(0, 4).map(risk => ({
            title: risk.title || risk.name,
            asset: risk.assetValue || risk.asset,
            severity: risk.severity || risk.priority,
            description: risk.description
        }));
    }, [topRisks]);

    // Generate recommendations
    const recommendations = useMemo(() => {
        return topRisks.slice(0, 5).map(risk => ({
            title: `Address ${risk.title || risk.name}`,
            asset: risk.assetValue || risk.asset,
            severity: risk.severity
        }));
    }, [topRisks]);

    // Today's date for briefing
    const briefingDate = new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    if (loading) {
        return (
            <div className="ib-page">
                <ClassificationBanner />
                <header className="ib-header">
                    <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                </header>
                <SkeletonDashboard />
            </div>
        );
    }

    if (error) {
        return (
            <div className="ib-page">
                <ClassificationBanner />
                <header className="ib-header">
                    <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                </header>
                <div className="ib-section">
                    <p style={{ color: 'var(--severity-high)', textAlign: 'center' }}>⚠️ {error}</p>
                    <button onClick={() => window.location.reload()} style={{ display: 'block', margin: '1rem auto' }}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="ib-page">
            <ClassificationBanner level="INTERNAL USE ONLY" />

            {/* Document Header */}
            <header className="ib-doc-header">
                <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>

                <div className="doc-title-block">
                    <div className="doc-logo">R3D</div>
                    <div className="doc-title-text">
                        <h1>Security Intelligence Brief</h1>
                        <div className="doc-subtitle">
                            External Attack Surface Assessment
                        </div>
                    </div>
                </div>

                <div className="doc-meta">
                    <div className="meta-item">
                        <span className="meta-label">Date:</span>
                        <span className="meta-value">{briefingDate}</span>
                    </div>
                    <div className="meta-item">
                        <span className="meta-label">Last Update:</span>
                        <span className="meta-value">{formatRelativeTime(summary.lastScanAt)}</span>
                    </div>
                    <div className="meta-item">
                        <span className="meta-label">Assets Monitored:</span>
                        <span className="meta-value">{summary.totalAssets || 0}</span>
                    </div>
                </div>
            </header>

            {/* Executive Summary */}
            <section className="ib-section ib-executive-summary">
                <h2 className="section-title">I. Executive Summary</h2>

                <div className={`assessment-banner ${overallAssessment.class}`}>
                    <div className="assessment-level">
                        Overall Assessment: <strong>{overallAssessment.level}</strong>
                    </div>
                    <div className="assessment-score">
                        Security Score: <strong>{summary.overallRiskScore || 0}/100</strong>
                    </div>
                </div>

                <p className="assessment-description">{overallAssessment.description}</p>

                <div className="summary-stats">
                    <table className="ib-table stats-table">
                        <tbody>
                            <MetricRow
                                label="Total Vulnerabilities"
                                value={(summary.totalVulnerabilities?.critical || 0) +
                                       (summary.totalVulnerabilities?.high || 0) +
                                       (summary.totalVulnerabilities?.medium || 0)}
                            />
                            <MetricRow
                                label="Critical Findings"
                                value={summary.totalVulnerabilities?.critical || 0}
                                severity="critical"
                            />
                            <MetricRow
                                label="High Findings"
                                value={summary.totalVulnerabilities?.high || 0}
                                severity="high"
                            />
                            <MetricRow
                                label="Medium Findings"
                                value={summary.totalVulnerabilities?.medium || 0}
                                severity="medium"
                            />
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Key Findings */}
            <section className="ib-section">
                <h2 className="section-title">II. Key Findings</h2>

                {keyFindings.length > 0 ? (
                    <div className="key-findings-list">
                        {keyFindings.map((finding, i) => (
                            <KeyFinding key={i} finding={finding} index={i} />
                        ))}
                    </div>
                ) : (
                    <p className="no-findings">No significant findings to report.</p>
                )}
            </section>

            {/* Asset Status */}
            <section className="ib-section">
                <h2 className="section-title">III. Asset Status</h2>

                <table className="ib-table asset-table">
                    <thead>
                        <tr>
                            <th>Asset</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Findings (C/H/M)</th>
                            <th>Last Scan</th>
                        </tr>
                    </thead>
                    <tbody>
                        {assets.slice(0, 8).map(asset => (
                            <AssetStatusRow
                                key={asset.assetId}
                                asset={asset}
                                onClick={() => navigate(`/assets/${asset.assetId}`)}
                            />
                        ))}
                    </tbody>
                </table>

                {assets.length > 8 && (
                    <button
                        className="view-all-btn"
                        onClick={() => navigate('/assets')}
                    >
                        View All {assets.length} Assets →
                    </button>
                )}
            </section>

            {/* Recommendations */}
            <section className="ib-section">
                <h2 className="section-title">IV. Recommended Actions</h2>

                {recommendations.length > 0 ? (
                    <div className="recommendations-list">
                        {recommendations.map((rec, i) => (
                            <Recommendation key={i} rec={rec} index={i} />
                        ))}
                    </div>
                ) : (
                    <p className="no-findings">No immediate actions required.</p>
                )}
            </section>

            {/* Footer */}
            <footer className="ib-footer">
                <div className="footer-note">
                    This brief is generated automatically based on the latest scan data.
                    For detailed analysis, review individual asset reports.
                </div>
                <div className="footer-actions">
                    <button
                        className="footer-btn primary"
                        onClick={() => navigate('/assets')}
                    >
                        View Assets
                    </button>
                    <button
                        className="footer-btn"
                        onClick={() => navigate('/inventory')}
                    >
                        Open Inventory
                    </button>
                    <button
                        className="footer-btn"
                        onClick={() => window.print()}
                    >
                        Print Brief
                    </button>
                </div>
            </footer>

            <ClassificationBanner level="INTERNAL USE ONLY" />
        </div>
    );
}

export default DashboardIntelBrief;
